export const FUNNEL_PROGRESS = {
  questionsComplete: 45,
  preMatchComplete: 55,
  results: 60,
  confirm: 95,
  complete: 100,
} as const;

export type ProgressDirection = "forward" | "back";
export type PhoneProgressStage = "phone" | "code";

export type FunnelProgressUpdate =
  | { phase: "results"; direction?: ProgressDirection }
  | {
      phase: "hosted" | "preMatchHosted";
      /** Zero-based presentation position; an included phone step has two stages. */
      stageIndex: number;
      totalStages: number;
      direction?: ProgressDirection;
    };

export interface QuestionProgressState {
  /** Completed screens on the current path, even if later answers hide them. */
  completedSteps: number[];
  percent: number;
}

export function boundProgress(percent: number): number {
  return Number.isFinite(percent) ? Math.max(0, Math.min(percent, 100)) : 0;
}

/** Remaining screens include the current grouped question page or interstitial. */
export function getQuestionProgress(completedScreens: number, remainingScreens: number): number {
  if (remainingScreens <= 0) return FUNNEL_PROGRESS.questionsComplete;
  return (
    (FUNNEL_PROGRESS.questionsComplete * (completedScreens + 1)) /
    (completedScreens + remainingScreens + 1)
  );
}

export function moveQuestionProgress(
  previous: QuestionProgressState,
  currentStep: number,
  nextStep: number,
  remainingScreens: number,
  direction: ProgressDirection,
  completeCurrentStep = true
): QuestionProgressState {
  const completedSteps =
    direction === "back"
      ? previous.completedSteps.filter((step) => step < nextStep)
      : !completeCurrentStep || previous.completedSteps.includes(currentStep)
        ? previous.completedSteps
        : [...previous.completedSteps, currentStep];
  const candidate = getQuestionProgress(completedSteps.length, remainingScreens);
  return {
    completedSteps,
    percent: direction === "back" ? candidate : Math.max(previous.percent, candidate),
  };
}

/** Expand the effective navigation steps without changing their order or skip rules. */
export function getHostedProgressPosition(
  steps: readonly string[],
  stepIndex: number,
  phoneStage: PhoneProgressStage
): { stageIndex: number; totalStages: number } {
  const totalStages = steps.reduce((count, step) => count + (step === "phone" ? 2 : 1), 0);
  const precedingStages = steps
    .slice(0, stepIndex)
    .reduce((count, step) => count + (step === "phone" ? 2 : 1), 0);
  return {
    stageIndex: precedingStages + (steps[stepIndex] === "phone" && phoneStage === "code" ? 1 : 0),
    totalStages,
  };
}

export function getStageProgress(update: FunnelProgressUpdate): number {
  if (update.phase === "results") return FUNNEL_PROGRESS.results;
  const start =
    update.phase === "hosted" ? FUNNEL_PROGRESS.results : FUNNEL_PROGRESS.questionsComplete;
  const end =
    update.phase === "hosted" ? FUNNEL_PROGRESS.confirm : FUNNEL_PROGRESS.preMatchComplete;
  if (update.totalStages <= 0) return start;
  // A hosted confirm-only path is still unfinished; identity collection is not completion.
  if (update.totalStages === 1) return update.phase === "hosted" ? end : start;
  const fraction = Math.max(0, Math.min(update.stageIndex / (update.totalStages - 1), 1));
  return start + (end - start) * fraction;
}

/** Only intentional Back resets the floor; changing visibility may cause a forward plateau. */
export function updateProgress(previous: number, update: FunnelProgressUpdate): number {
  const candidate = getStageProgress(update);
  return update.direction === "back" ? candidate : Math.max(previous, candidate);
}
